import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, Users, Sparkles, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { SEONavbar, SEOFooter } from '../components/SEOLayout';
import PublicChatWidget from '../components/PublicChatWidget';

const PLANS = [
  {
    key: 'start',
    name: 'Start',
    basePrice: 1990,
    perEmployee: 199,
    desc: 'För enmans- och småföretag.',
    features: [
      'AI kalkyl & material­beräkning',
      'GPS-tidrapportering',
      'Fakturering med ROT',
      'Kundportal',
      'BankID-signering av ÄTA',
      'E-postsupport',
    ],
    missing: ['AI-bokföring', 'API-tillgång'],
    cta: 'Börja gratis',
    highlight: false,
  },
  {
    key: 'pro',
    name: 'Pro',
    basePrice: 2990,
    perEmployee: 149,
    desc: 'För växande byggföretag.',
    features: [
      'Allt i Start',
      'AI-bokföring (BAS-kontoplan)',
      'Datapimport (Fortnox/Bokio)',
      'Live GPS-karta för admin',
      'Push-notiser',
      'Prioriterad support',
    ],
    missing: ['Vita-etikett (white-label)'],
    cta: 'Välj Pro',
    highlight: true,
  },
  {
    key: 'business',
    name: 'Business',
    basePrice: 4490,
    perEmployee: 99,
    desc: 'För etablerade entreprenörer.',
    features: [
      'Allt i Pro',
      'API-tillgång',
      'White-label (egen logo + domän)',
      'Dedikerad kundansvarig',
      'Onboarding & utbildning',
      '99,9% SLA',
    ],
    missing: [],
    cta: 'Kontakta sälj',
    highlight: false,
  },
];

const FAQ = [
  { q: 'Vad ingår i 14-dagars provperiod?', a: 'Alla funktioner i Pro-paketet, inga begränsningar. Inom 14 dagar väljer du att fortsätta (då sätter du upp betalning) eller avsluta utan kostnad.' },
  { q: 'Hur fungerar prissättningen för extra användare?', a: '1 användare ingår alltid i baspaketet. Extra användare kostar 199 kr/mån (Start), 149 kr/mån (Pro) eller 99 kr/mån (Business). Du lägger till och tar bort användare när du vill direkt i Operio — ingen bindningstid på extra användare. Vid borttagning gäller 1 månads uppsägningstid.' },
  { q: 'Är det bindningstid?', a: 'Baspaketet löper på 12 månader (standard) eller 6 mån (+10 %) eller 24 mån (–15 %). Extra användare har INGEN bindning — du kan lägga till och ta bort dem när som helst med 1 månads uppsägning.' },
  { q: 'Kan jag byta paket senare?', a: 'Ja, uppgradering eller nedgradering sker direkt i Inställningar → Prenumeration. Vi pro-ratar avgiften så du betalar exakt rätt.' },
  { q: 'Tillkommer det moms?', a: 'Ja, alla priser är exkl. 25 % moms.' },
  { q: 'Finns det rabatt för flera projekt?', a: 'Vi har volymrabatter för >50 anställda och bygg-koncerner. Kontakta kontakt@operio.nu för offert.' },
];

const PrisPage = () => {
  const [employees, setEmployees] = useState(1);

  const totals = useMemo(() => {
    return PLANS.reduce((acc, p) => {
      const extras = Math.max(0, employees - 1);
      acc[p.key] = p.basePrice + extras * p.perEmployee;
      return acc;
    }, {});
  }, [employees]);

  return (
  <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
    <SEO
      title="Priser & paket – Operio bygg-SaaS från 1 990 kr/mån exkl. moms"
      description="Tre paket: Start (1 990 kr), Pro (2 990 kr) och Business (4 490 kr) — alla priser exkl. 25 % moms. 1 användare ingår, extra användare från 99 kr/mån. 14 dagar gratis."
      canonical="/priser"
      keywords="operio pris, bygg saas pris, bygg administration kostnad, tidrapportering pris bygg"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Operio',
        description: 'AI-plattform för svenska bygg-, mark- och entreprenadföretag.',
        brand: { '@type': 'Brand', name: 'Operio AB' },
        offers: PLANS.map((p) => ({
          '@type': 'Offer',
          name: p.name,
          price: p.basePrice,
          priceCurrency: 'SEK',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: p.basePrice, priceCurrency: 'SEK', unitText: 'MONTH' },
        })),
      }}
    />
    <SEONavbar activePage="priser" />

    <main className="pt-24 sm:pt-28 pb-20">
      <section className="px-5 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-4">
            <Sparkles size={14} /> Inga dolda avgifter
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Priser som växer med ditt företag
          </h1>
          <p className="text-base sm:text-lg text-[#6B6560] mt-5 max-w-2xl mx-auto leading-relaxed">
            14 dagar gratis — sedan från 1 990 kr/månad exkl. moms. 1 användare ingår. Lägg till fler när du behöver.
          </p>
        </div>
      </section>

      {/* Interactive calculator */}
      <section className="px-5 sm:px-6 mt-10">
        <div className="max-w-xl mx-auto bg-white border border-[#E8E2D9] rounded-2xl p-5 sm:p-6" data-testid="price-calculator">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A]">Räkna ditt pris</p>
              <p className="text-sm text-[#1A1A1A] mt-1">Hur många användare behöver du?</p>
              <p className="text-xs text-[#7A7570] mt-0.5">1 användare ingår alltid i baspaketet.</p>
            </div>
            <div className="flex items-center gap-2 bg-[#F7F4EF] rounded-full px-2 py-1.5">
              <button
                type="button"
                onClick={() => setEmployees((n) => Math.max(1, n - 1))}
                className="w-8 h-8 rounded-full bg-white border border-[#E8E2D9] flex items-center justify-center hover:bg-[#E8613A] hover:text-white hover:border-[#E8613A] transition"
                data-testid="calc-decrement"
                aria-label="Färre användare"
              ><Minus size={14} /></button>
              <span className="w-10 text-center font-bold text-lg text-[#1A1A1A]" data-testid="calc-count">{employees}</span>
              <button
                type="button"
                onClick={() => setEmployees((n) => Math.min(99, n + 1))}
                className="w-8 h-8 rounded-full bg-white border border-[#E8E2D9] flex items-center justify-center hover:bg-[#E8613A] hover:text-white hover:border-[#E8613A] transition"
                data-testid="calc-increment"
                aria-label="Fler användare"
              ><Plus size={14} /></button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((p) => (
            <article
              key={p.key}
              className={`bg-white rounded-2xl border p-7 flex flex-col relative ${
                p.highlight ? 'border-[#E8613A] shadow-xl shadow-[#E8613A]/15 md:scale-[1.03]' : 'border-[#E8E2D9]'
              }`}
              data-testid={`price-card-${p.key}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E8613A] text-white text-xs font-bold px-3 py-1 rounded-full">POPULÄRAST</span>
              )}
              <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{p.name}</h2>
              <p className="text-sm text-[#6B6560] mt-1">{p.desc}</p>

              {/* Dynamic total based on employee count */}
              <p className="mt-5">
                <span className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid={`price-total-${p.key}`}>
                  {totals[p.key].toLocaleString('sv-SE')}
                </span>
                <span className="text-sm text-[#6B6560]"> kr/mån</span>
              </p>
              <p className="text-xs text-[#6B6560]">exkl. 25 % moms · {employees} {employees === 1 ? 'användare' : 'användare'}</p>

              {/* Breakdown — clear contrast against white card */}
              <div className="mt-3 border-2 border-[#1A1A1A]/10 bg-[#1A1A1A]/[0.03] rounded-lg px-3 py-2.5 text-xs space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-[#1A1A1A]">Baspaket <span className="text-[#7A7570]">(1 användare ingår)</span></span>
                  <span className="font-bold text-[#1A1A1A]">{p.basePrice.toLocaleString('sv-SE')} kr</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#1A1A1A]">{Math.max(0, employees - 1)} × extra användare</span>
                  <span className="font-bold text-[#1A1A1A]">+{(Math.max(0, employees - 1) * p.perEmployee).toLocaleString('sv-SE')} kr</span>
                </div>
                <p className="text-[10px] text-[#7A7570] pt-1.5 border-t border-[#1A1A1A]/10 mt-1.5">
                  Extra användare: {p.perEmployee} kr/mån · ingen bindning · 1 mån uppsägning
                </p>
              </div>

              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#1A1A1A]">
                    <Check size={16} className="text-[#E8613A] flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
                {p.missing.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#7A7570]/70 line-through">
                    <X size={16} className="text-[#7A7570]/40 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.name === 'Business' ? '/kontakt' : '/register'} className="mt-6 block">
                <Button className={`w-full h-11 rounded-full font-semibold ${
                  p.highlight ? 'bg-[#E8613A] hover:bg-[#D4532F] text-white' : 'bg-[#1A1A1A] hover:bg-[#333] text-white'
                }`} data-testid={`select-${p.key}`}>
                  {p.cta} <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-16">
        <div className="max-w-3xl mx-auto bg-white border border-[#E8E2D9] rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 bg-[#E8613A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Users size={22} className="text-[#E8613A]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#1A1A1A]">Mer än 50 anställda?</h3>
            <p className="text-sm text-[#6B6560] mt-1">Vi erbjuder volym­rabatter och Enterprise-stöd. Kontakta oss för anpassad offert.</p>
          </div>
          <Link to="/kontakt"><Button variant="outline" className="rounded-full">Kontakta sälj</Button></Link>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-8 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vanliga frågor om priser
          </h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group bg-white border border-[#E8E2D9] rounded-xl px-5 py-4 open:shadow-sm">
                <summary className="cursor-pointer text-base font-bold text-[#1A1A1A] flex justify-between items-center list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-[#E8613A] text-xl flex-shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>

    <SEOFooter />
    <PublicChatWidget />
  </div>
  );
};

export default PrisPage;
